import type { TypeTheme } from "@revolt/state/stores/Theme";

import type {
  LayoutKey,
  PresenceKey,
  RadiusKey,
  ShadowKey,
  SpaceKey,
} from "./keys";

/**
 * Current version of the theme definition format
 */
export const THEME_DEFINITION_VERSION = 1;

/**
 * Complete, serialisable description of a theme
 *
 * Everything visual is derived from this object by the theme engine,
 * which turns it into `--typ-*` CSS variables.
 */
export interface ThemeDefinition {
  /**
   * Format version, bumped on breaking changes
   */
  version: typeof THEME_DEFINITION_VERSION;

  meta: {
    /**
     * Unique identifier of the theme
     */
    id: string;

    /**
     * Human readable name
     */
    name: string;
  };

  /**
   * Whether this theme renders in dark mode
   */
  darkMode: boolean;

  colors: {
    /**
     * Source colour the palette is generated from (hex)
     */
    source: string;

    /**
     * Optional accent colour (hex) the tertiary roles are generated from,
     * uses the palette's own tertiary colours when omitted
     */
    accent?: string;

    /**
     * Optional chroma used to tint neutral surfaces with the source hue,
     * keeps the palette's own neutrals when omitted
     */
    surfaceChroma?: number;

    /**
     * Contrast level (-1 to 1)
     */
    contrast: number;

    /**
     * Palette generation variant
     */
    variant: TypeTheme["m3Variant"];

    /**
     * Presence indicator colours
     */
    presence: Record<PresenceKey, string>;
  };

  /**
   * Named gradients, e.g. `app`, `sidebar`, `auth`
   */
  gradients: Record<string, string>;

  /**
   * Named backgrounds, e.g. `app`, `auth`
   */
  backgrounds: Record<string, string>;

  typography: {
    /**
     * Interface font family (first choice)
     */
    ui: string;

    /**
     * Monospace font family (first choice)
     */
    mono: string;

    /**
     * Message font size in pixels
     */
    messageSize: number;

    /**
     * Extra letter spacing added to all interface text (CSS length)
     */
    tracking: string;

    /**
     * Emoji sizes
     */
    emoji: {
      inline: string;
      medium: string;
      large: string;
    };
  };

  radius: Record<RadiusKey, string>;

  spacing: {
    scale: Record<SpaceKey, string>;

    /**
     * Space between message groups in pixels
     */
    messageGroup: number;

    layout: Record<LayoutKey, string>;
  };

  borders: {
    width: string;
  };

  shadows: Record<ShadowKey, string>;

  motion: {
    fast: string;
    medium: string;

    /**
     * Multiplier for animation intensity (0 disables motion)
     */
    scale: number;
  };

  effects: {
    /**
     * Whether blurry surfaces are enabled
     */
    blur: boolean;
  };

  /**
   * Image shown behind the app, panels turn translucent when set
   */
  wallpaper?: {
    /**
     * Image URL
     */
    url: string;

    /**
     * Darkening applied over the image (0-80 %)
     */
    dim: number;

    /**
     * Blur behind the panels (px)
     */
    blur: number;

    /**
     * Panel opacity (40-100 %)
     */
    panelOpacity: number;
  };

  /**
   * Per-component overrides, exposed as `--typ-c-<component>-<property>`
   */
  components: Record<string, Record<string, string>>;
}
